import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactDOM from "react-dom";
import { apiCall } from "../../../_services/apiCall";
import config from "../../../config/config";
import TagFilterBar from "./TagFilterBar";
import { questionMatchesSteps } from "./TagFilterUtils";

const API = config.POSTLOGIN_API_URL_COMPANY;
const BRAND = "#3f88a5";

/* ── ToggleSwitch ── */
const ToggleSwitch = ({ active, toggling, onClick }) => (
  <div
    onClick={() => !toggling && onClick()}
    style={{
      width: 36, height: 20, borderRadius: 10,
      cursor: toggling ? "not-allowed" : "pointer",
      background: active ? BRAND : "#d1d5db",
      position: "relative", transition: "background 0.2s",
      userSelect: "none", flexShrink: 0,
    }}
  >
    <span style={{
      position: "absolute", top: 2,
      left: active ? 18 : 2,
      width: 16, height: 16, borderRadius: "50%",
      background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      transition: "left 0.2s", display: "block",
    }} />
  </div>
);

/* ── CustomDenomRow ── */
const CustomDenomRow = ({ d, i, total, onDelete }) => (
  <div
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "9px 16px",
      borderBottom: i < total - 1 ? "1px solid #f1f5f9" : "none",
      background: "#fff", transition: "background 0.1s",
      borderRadius: i === total - 1 ? "0 0 8px 8px" : 0,
    }}
    onMouseEnter={(e) => { e.currentTarget.style.background = "#f8fafc"; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{
        width: 20, height: 20, borderRadius: "50%", flexShrink: 0,
        background: "rgba(63,136,165,0.12)", color: BRAND,
        fontSize: 10, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>{i + 1}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>{d.title}</span>
    </div>
    <div
      onClick={() => onDelete(d.id)}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "4px 10px", borderRadius: 6,
        border: "1.5px solid #fecaca", background: "#fef2f2",
        color: "#dc2626", fontSize: 11, fontWeight: 600,
        cursor: "pointer", userSelect: "none",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "#fee2e2"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "#fef2f2"; }}
    >
      ✕ Remove
    </div>
  </div>
);

/* ── IndustryDenomRow ── */
const IndustryDenomRow = ({ d, i, total, toggling, onToggle }) => (
  <div
    style={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "10px 16px",
      borderBottom: i < total - 1 ? "1px solid #bae6fd" : "none",
      fontSize: 13, background: "#f0f9ff",
      opacity: d.isActive ? 1 : 0.5, transition: "opacity 0.2s",
      borderRadius: i === total - 1 ? "0 0 8px 8px" : 0,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{
        width: 6, height: 6, borderRadius: "50%", flexShrink: 0,
        background: d.isActive ? "#22c55e" : "#d1d5db",
        display: "inline-block",
      }} />
      <span style={{ fontWeight: 500, color: "#1e293b" }}>{d.title}</span>
    </div>
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
      <span style={{
        fontSize: 10, color: "#0369a1", background: "#e0f2fe",
        borderRadius: 12, padding: "2px 8px", fontWeight: 700,
        border: "1px solid #bae6fd",
      }}>Industry</span>
      <ToggleSwitch active={d.isActive} toggling={toggling} onClick={() => onToggle(d)} />
    </div>
  </div>
);

/* ── QuestionItem ── */
const QuestionItem = ({ q, idx, total, isChecked, isLoading, addingIds, onToggle }) => (
  <div
    onMouseDown={() => !isLoading && onToggle(q)}
    style={{
      padding: "8px 12px", cursor: isLoading ? "not-allowed" : "pointer",
      borderBottom: idx < total - 1 ? "1px solid #f8fafc" : "none",
      display: "flex", gap: 10, alignItems: "flex-start",
      opacity: isLoading ? 0.6 : 1,
      background: isChecked ? "rgba(63,136,165,0.05)" : "#fff",
      userSelect: "none", transition: "background 0.1s",
    }}
    onMouseEnter={(e) => {
      if (!isLoading) e.currentTarget.style.background = isChecked ? "rgba(63,136,165,0.1)" : "#f8fafc";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.background = isChecked ? "rgba(63,136,165,0.05)" : "#fff";
    }}
  >
    <div style={{
      width: 15, height: 15, marginTop: 2, flexShrink: 0, borderRadius: 4,
      border: `2px solid ${isChecked ? BRAND : "#cbd5e1"}`,
      background: isChecked ? BRAND : "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      transition: "all 0.14s",
    }}>
      {isChecked && (
        <svg width="8" height="6" viewBox="0 0 8 6" fill="none" style={{ display: "block" }}>
          <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: isChecked ? 600 : 400, color: "#1e293b", lineHeight: 1.4 }}>{q.title}</div>
      {q.moduleName && <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{q.moduleName}</div>}
    </div>
    {isLoading && (
      <span style={{ fontSize: 11, color: BRAND, flexShrink: 0, alignSelf: "center" }}>
        {addingIds.has(q.id) ? "Adding…" : "Removing…"}
      </span>
    )}
  </div>
);

/* ── QuestionDropdown ── */
const QuestionDropdown = ({ questions, addedIds, addingIds, removingIds, onToggle }) => {
  const [open, setOpen]           = useState(false);
  const [search, setSearch]       = useState("");
  const [popoverStyle, setPopoverStyle] = useState({});
  const triggerRef                = useRef(null);
  const inputRef                  = useRef(null);

  useEffect(() => {
    if (open && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPopoverStyle({ position: "fixed", top: rect.bottom + 3, left: rect.left, width: rect.width });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        !document.getElementById("qd-portal")?.contains(e.target)
      ) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = search.trim()
    ? questions.filter((q) => (q.title || "").toLowerCase().includes(search.trim().toLowerCase()))
    : questions;

  const checkedCount = questions.filter((q) => addedIds.has(q.id)).length;

  const popover = open && ReactDOM.createPortal(
    <div
      id="qd-portal"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        ...popoverStyle,
        background: "#fff", border: `1.5px solid ${BRAND}`,
        borderRadius: 8, zIndex: 9999,
        boxShadow: "0 6px 24px rgba(0,0,0,0.14)", overflow: "hidden",
      }}
    >
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search questions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%", padding: "6px 10px",
            border: "1.5px solid #e2e8f0", borderRadius: 6,
            fontSize: 13, outline: "none",
            color: "#1e293b", background: "#fff",
            fontFamily: "inherit", boxSizing: "border-box",
          }}
          onFocus={(e) => { e.target.style.borderColor = BRAND; }}
          onBlur={(e)  => { e.target.style.borderColor = "#e2e8f0"; }}
        />
      </div>
      <div style={{ maxHeight: 240, overflowY: "auto" }}>
        {filtered.length === 0 ? (
          <div style={{ padding: "12px 14px", color: "#94a3b8", fontSize: 13, textAlign: "center" }}>
            No questions found
          </div>
        ) : (
          filtered.map((q, idx) => (
            <QuestionItem
              key={q.id}
              q={q} idx={idx} total={filtered.length}
              isChecked={addedIds.has(q.id)}
              isLoading={addingIds.has(q.id) || removingIds.has(q.id)}
              addingIds={addingIds}
              onToggle={onToggle}
            />
          ))
        )}
      </div>
      <div style={{
        padding: "5px 12px", borderTop: "1px solid #f1f5f9",
        background: "#f8fafc", display: "flex", justifyContent: "space-between",
        fontSize: 11, color: "#94a3b8",
      }}>
        <span>{filtered.length} shown</span>
        {checkedCount > 0 && <span style={{ color: BRAND, fontWeight: 700 }}>{checkedCount} selected</span>}
      </div>
    </div>,
    document.body
  );

  return (
    <>
      <div
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "7px 11px",
          border: `1.5px solid ${open ? BRAND : "#d1d5db"}`,
          borderRadius: 7, background: "#fff",
          cursor: "pointer", userSelect: "none", minHeight: 36,
          boxShadow: open ? `0 0 0 3px rgba(63,136,165,0.12)` : "none",
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxSizing: "border-box",
        }}
      >
        <span style={{ fontSize: 13, color: checkedCount > 0 ? "#1e293b" : "#94a3b8" }}>
          {checkedCount > 0
            ? <><span style={{ color: "#94a3b8" }}>Questions — </span><span style={{ fontWeight: 600, color: BRAND }}>{checkedCount} selected</span></>
            : "Select questions…"}
        </span>
        <span style={{
          fontSize: 10, color: "#94a3b8", marginLeft: 6,
          transition: "transform 0.15s",
          transform: open ? "rotate(180deg)" : "rotate(0deg)",
          display: "inline-block",
        }}>▾</span>
      </div>
      {popover}
    </>
  );
};

/* ══ MAIN COMPONENT ══ */
const IntensityDenominatorConfig = ({ denominators, onRefresh }) => {
  const [allQuestions, setAllQuestions] = useState([]);
  const [togglingId,   setTogglingId]   = useState(null);
  const [addingIds,    setAddingIds]    = useState(new Set());
  const [removingIds,  setRemovingIds]  = useState(new Set());
  const [loading,      setLoading]      = useState(true);
  const [filterSteps,  setFilterSteps]  = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { isSuccess, data } = await apiCall(`${API}intensity/denominator-questions`, {}, {}, "GET");
      if (isSuccess) setAllQuestions(data?.data || []);
      setLoading(false);
    })();
  }, []);

  const customDenominators = useMemo(() => denominators.filter((d) => !d.isPredefined), [denominators]);
  const industryDenoms     = useMemo(() => denominators.filter((d) =>  d.isPredefined), [denominators]);
  const addedIds           = useMemo(() => new Set(customDenominators.map((d) => d.questionId)), [customDenominators]);

  const questionsWithTags = useMemo(
    () => allQuestions.map((q) => {
      const chain = [];
      if (q.moduleName) chain.push({ key: "module", value: q.moduleName });
      if (q.category)   chain.push({ key: "category", value: q.category });
      if (q.subCategory && q.subCategory !== "NULL")
        chain.push({ key: "subCategory", value: q.subCategory });
      return { ...q, tags: chain.length ? [chain] : null };
    }),
    [allQuestions]
  );

  const visibleQuestions = useMemo(
    () => questionsWithTags.filter((q) => questionMatchesSteps(q, filterSteps)),
    [questionsWithTags, filterSteps]
  );

  const questionMap = useMemo(
    () => new Map(questionsWithTags.map((q) => [q.id, q])),
    [questionsWithTags]
  );

  const visibleDenominators = useMemo(() => {
    if (!filterSteps.some((s) => s.values?.size)) return customDenominators;
    return customDenominators.filter((d) => {
      const q = questionMap.get(d.questionId);
      return q ? questionMatchesSteps(q, filterSteps) : true;
    });
  }, [customDenominators, filterSteps, questionMap]);

  const handleToggle = async (q) => {
    if (addedIds.has(q.id)) {
      const denom = customDenominators.find((d) => d.questionId === q.id);
      if (!denom) return;
      setRemovingIds((prev) => new Set(prev).add(q.id));
      try {
        await apiCall(`${API}intensity/denominator-config/${denom.id}`, {}, {}, "DELETE", true, false);
        onRefresh();
      } finally {
        setRemovingIds((prev) => { const next = new Set(prev); next.delete(q.id); return next; });
      }
    } else {
      setAddingIds((prev) => new Set(prev).add(q.id));
      try {
        const { isSuccess } = await apiCall(
          `${API}intensity/denominator-config`,
          {}, { questionId: q.id, title: q.title },
          "POST", true, false
        );
        if (isSuccess) onRefresh();
      } finally {
        setAddingIds((prev) => { const next = new Set(prev); next.delete(q.id); return next; });
      }
    }
  };

  const handleToggleIndustry = async (d) => {
    setTogglingId(d.id);
    const { isSuccess } = await apiCall(
      `${API}intensity/industry-denominators/${d.id}`,
      {}, { isActive: !d.isActive }, "PATCH"
    );
    if (isSuccess) onRefresh();
    setTogglingId(null);
  };

  const handleDelete = async (id) => {
    await apiCall(`${API}intensity/denominator-config/${id}`, {}, {}, "DELETE");
    onRefresh();
  };

  return (
    <div style={{ fontFamily: "inherit" }}>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#1e293b", marginBottom: 2 }}>Denominator Configuration</div>
        <div style={{ fontSize: 12, color: "#94a3b8" }}>Configure which questions are used as intensity denominators.</div>
      </div>

      {/* ── Custom Denominators ── */}
      <div style={{ border: "1.5px solid #e2e8f0", borderRadius: 10, marginBottom: 20 }}>
        <div style={{
          padding: "11px 16px", borderBottom: "1.5px solid #e2e8f0",
          background: "#f8fafc", borderRadius: "8px 8px 0 0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Custom Denominators</div>
            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 1 }}>Filter by module / category, then select questions to add</div>
          </div>
          {customDenominators.length > 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, background: BRAND, color: "#fff", borderRadius: 12, padding: "2px 9px" }}>
              {customDenominators.length} configured
            </span>
          )}
        </div>

        <div style={{ padding: "12px 16px", background: "#fff", display: "flex", alignItems: "center", gap: 12 }}>
          {loading ? (
            <div style={{
              width: 220, flexShrink: 0,
              border: "1.5px solid #e2e8f0", borderRadius: 7, padding: "8px 12px",
              fontSize: 13, color: "#94a3b8", background: "#f8fafc",
            }}>Loading…</div>
          ) : (
            <div style={{ width: 220, flexShrink: 0 }}>
              <QuestionDropdown
                questions={visibleQuestions}
                addedIds={addedIds}
                addingIds={addingIds}
                removingIds={removingIds}
                onToggle={handleToggle}
              />
            </div>
          )}
          {!loading && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <TagFilterBar
                allQuestions={questionsWithTags}
                filterSteps={filterSteps}
                onFilterStepsChange={setFilterSteps}
              />
            </div>
          )}
        </div>

        {customDenominators.length > 0 && (
          <div style={{ borderTop: "1.5px solid #e2e8f0" }}>
            <div style={{
              padding: "7px 16px", background: "#f8fafc",
              fontSize: 10, fontWeight: 700, color: "#94a3b8",
              textTransform: "uppercase", letterSpacing: "0.06em",
              borderBottom: "1px solid #e2e8f0",
            }}>Configured</div>
            {visibleDenominators.length === 0 ? (
              <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", textAlign: "center", background: "#fff" }}>
                No configured denominators match the current filter.
              </div>
            ) : (
              visibleDenominators.map((d, i) => (
                <CustomDenomRow key={d.id} d={d} i={i} total={visibleDenominators.length} onDelete={handleDelete} />
              ))
            )}
          </div>
        )}

        {customDenominators.length === 0 && !loading && (
          <div style={{
            padding: "16px", textAlign: "center", fontSize: 13, color: "#94a3b8",
            borderTop: "1.5px solid #e2e8f0", background: "#f8fafc", borderRadius: "0 0 8px 8px",
          }}>
            No custom denominators configured yet. Select questions above to add.
          </div>
        )}
      </div>

      {/* ── Industry Default Denominators ── */}
      <div style={{ border: "1.5px solid #bae6fd", borderRadius: 10 }}>
        <div style={{
          padding: "11px 16px", borderBottom: "1.5px solid #bae6fd",
          background: "#f0f9ff", borderRadius: "8px 8px 0 0",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>Industry Default Denominators</span>
              <span style={{
                fontSize: 10, background: "#dbeafe", color: "#2563eb",
                border: "1px solid #bfdbfe", borderRadius: 20, padding: "2px 8px", fontWeight: 700,
              }}>Auto-included</span>
            </div>
            <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2 }}>
              Automatically shown in Quick View based on your industry profile
            </div>
          </div>
        </div>

        {industryDenoms.length === 0 ? (
          <div style={{ padding: "14px 16px", fontSize: 13, color: "#94a3b8", background: "#f0f9ff", borderRadius: "0 0 8px 8px" }}>
            No industry defaults found for your sector.
          </div>
        ) : (
          industryDenoms.map((d, i) => (
            <IndustryDenomRow
              key={d.id} d={d} i={i} total={industryDenoms.length}
              toggling={togglingId === d.id}
              onToggle={handleToggleIndustry}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default IntensityDenominatorConfig;
